import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ALL_STATES, DATE_REQUIRED_STATES, STATE_STYLE, SEGMENT_STYLE, LEAD_SOURCES, LEAD_TYPES,
  CAN_ASSIGN_ROLES, CAN_MARK_HOT_ROLES, BACKEND_ASSIGNABLE_ROLES, canViewAI, isSuperStore, STORE_SUPER,
  fmtDate, fmtDateTime, fmtINR,
} from "../lib/states";
import { useRequirements } from "../lib/requirements";
import {
  X, Phone, MessageSquare, Clock, MapPin, Tag, ExternalLink, Sparkles, Check,
  ShoppingBag, Loader2, Flame, Lock, Save, UserCog, Trash2, AlertTriangle, Store, IndianRupee, Handshake,
} from "lucide-react";

const EVENT_LABEL = {
  created: "Lead arrived", state_change: "Stage changed", assigned: "Assigned",
  action: "Action logged", research: "Research", login: "Login", hot: "Hot status",
};

export default function LeadDrawer({ leadId, presetState, onClose, onSaved }) {
  const { user } = useAuth();
  const role = user?.role;
  const requirements = useRequirements();
  const [tab, setTab] = useState("lead");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [conv, setConv] = useState(null);
  const [convLoaded, setConvLoaded] = useState(false);
  const [researchStarted, setResearchStarted] = useState(false);

  const [actionType, setActionType] = useState("");
  const [newState, setNewState] = useState(presetState || "");
  const [nextDate, setNextDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const [backendId, setBackendId] = useState("");
  const [interId, setInterId] = useState("");
  const [links, setLinks] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichTimedOut, setEnrichTimedOut] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [enrichForm, setEnrichForm] = useState({ profession: "", employer: "", segment: "" });
  const [stores, setStores] = useState([]);
  const [savingStore, setSavingStore] = useState(false);
  const [pvValue, setPvValue] = useState("");
  const [savingPv, setSavingPv] = useState(false);

  const canAssign = CAN_ASSIGN_ROLES.includes(role);
  const canAssignStore = isSuperStore(user);  // Admins always are; mirrors backend.

  // Load the stores list once for the Super-Store assign dropdown. Non-Super
  // users never call this (the field stays hidden).
  useEffect(() => {
    if (!canAssignStore) return;
    api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});
  }, [canAssignStore]);

  const savePotentialValue = async () => {
    const raw = (pvValue ?? "").toString().trim();
    const num = raw === "" ? null : Number(raw);
    if (raw !== "" && (Number.isNaN(num) || num < 0)) {
      toast.error("Enter a valid amount in ₹");
      return;
    }
    setSavingPv(true);
    try {
      const { data: updated } = await api.patch(`/leads/${leadId}/potential-value`,
        { potential_order_value: num });
      setData((d) => (d ? { ...d, lead: updated } : d));
      toast.success(num ? `Potential value set to ₹${num.toLocaleString("en-IN")}` : "Potential value cleared");
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save potential value");
    } finally {
      setSavingPv(false);
    }
  };

  const changeStore = async (newStore) => {
    setSavingStore(true);
    try {
      const { data: updated } = await api.patch(`/leads/${leadId}/store`, {
        store: newStore || null,
      });
      setData((d) => (d ? { ...d, lead: updated } : d));
      toast.success(newStore ? `Lead moved to ${newStore}` : "Store unassigned");
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not update store");
    } finally {
      setSavingStore(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/journey`);
      setData(data);
      setEnrichTimedOut(false);
      setNewState((prev) => prev || data.lead.state);
      setBackendId(data.lead.assigned_to || "");
      setInterId(data.lead.interaction_assignee || "");
      setPvValue(data.lead.potential_order_value != null ? String(data.lead.potential_order_value) : "");
      // Acknowledge a new hot lead when a handler/admin opens it (clears the badge).
      if (data.lead.is_hot && !data.lead.hot_acknowledged && ["admin", "funnel_manager"].includes(role)) {
        api.post(`/leads/${leadId}/hot/ack`).catch(() => {});
      }
      if (canAssign) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [leadId, role, canAssign]);

  useEffect(() => { load(); }, [load]);

  // Lazy: deal/order cross-map is only fetched the first time the Journey tab is opened.
  useEffect(() => {
    if (tab !== "journey" || convLoaded || !data?.lead) return;
    setConvLoaded(true);
    api.get(`/leads/${leadId}/conversion`).then((r) => setConv(r.data.conversion)).catch(() => {});
  }, [tab, convLoaded, data?.lead, leadId]);

  // Lazy: research links + (if needed) auto-enrichment only start when the Research tab opens.
  useEffect(() => {
    if (tab !== "research" || researchStarted || !data?.lead) return;
    setResearchStarted(true);
    if (CAN_ASSIGN_ROLES.includes(role)) {
      api.get(`/leads/${leadId}/research-links`).then((r) => setLinks(r.data)).catch(() => {});
    }
  }, [tab, researchStarted, data?.lead, leadId, role]);

  // Poll while auto-enrichment is still running (no enriched_at yet). Also kicks off
  // enrichment for any lead that was never enriched (seeded/legacy/intake), so the user
  // never has to manually Re-run.
  const pollEnrichment = useCallback(() => {
    api.post(`/leads/${leadId}/auto-enrich`).catch(() => {});
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data: fresh } = await api.get(`/leads/${leadId}/journey`);
        if (fresh.lead.enriched_at) {
          setData(fresh);
          clearInterval(iv);
        } else if (tries >= 20) {
          clearInterval(iv);
          setEnrichTimedOut(true);
        }
      } catch {
        clearInterval(iv);
        setEnrichTimedOut(true);
      }
    }, 4000);
    return iv;
  }, [leadId]);

  useEffect(() => {
    if (tab !== "research" || !data?.lead || data.lead.enriched_at) return undefined;
    const iv = pollEnrichment();
    return () => clearInterval(iv);
  }, [tab, data?.lead?.enriched_at, leadId, pollEnrichment]);

  const lead = data?.lead;
  const canEdit = lead?.can_edit;
  const dateRequired = DATE_REQUIRED_STATES.includes(newState);
  const backendUsers = users.filter((u) => BACKEND_ASSIGNABLE_ROLES.includes(u.role) && u.active !== false);
  const interactionUsers = users.filter((u) => u.role === "interaction_staff" && u.active !== false);

  const showHotButton = lead && CAN_MARK_HOT_ROLES.includes(role) &&
    !(role === "backend_staff" && lead.is_hot); // backend staff can't unflag

  const saveAction = async () => {
    // Only enforce the next-action-date requirement when the user is
    // actually MOVING the lead into a date-required state. A plain "Report
    // activity" click (state unchanged) must NEVER be blocked, even when the
    // current state happens to be one that requires a date.
    const stateChanged = newState !== lead?.state;
    if (stateChanged && dateRequired && !nextDate) {
      return toast.error(`A next-action date is required for "${newState}".`);
    }
    setSaving(true);
    try {
      // If the user changed the backend / interaction assignee dropdowns but
      // didn't click the dedicated "Save assignment" button, persist that
      // change here too — otherwise the dropdown silently reverts on reload.
      const currentBackend = lead?.assigned_to || "";
      const currentInter = lead?.interaction_assignee || "";
      const assigneeChanged = canAssign && (backendId !== currentBackend || interId !== currentInter);
      if (assigneeChanged) {
        await api.patch(`/leads/${leadId}/assign`, {
          backend_assignee: backendId || null,
          interaction_assignee: interId || null,
        });
      }
      await api.post(`/leads/${leadId}/action`, {
        action_type: actionType || null, new_state: newState,
        next_action_date: dateRequired ? nextDate : null, note: note || null,
      });
      // Terser confirmation for the pure-report case; keeps the toast honest.
      const isSilentReport = !stateChanged && !actionType && !note && !assigneeChanged;
      toast.success(isSilentReport
        ? "Activity reported"
        : assigneeChanged ? "Lead and assignment updated" : "Lead updated");
      setNote("");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async () => {
    setDeleting(true);
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  const saveAssign = async () => {
    try {
      await api.patch(`/leads/${leadId}/assign`, {
        backend_assignee: backendId || null,
        interaction_assignee: interId || null,
      });
      toast.success("Assignment updated");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleHot = async () => {    try {
      await api.post(`/leads/${leadId}/hot`, { is_hot: !lead.is_hot });
      toast.success(!lead.is_hot ? "Marked as HOT" : "Removed HOT status");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const changeSource = async (src) => {
    try {
      await api.patch(`/leads/${leadId}/source`, { source: src });
      toast.success(`Source set to ${src}`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const changeLeadType = async (lt) => {
    if (!lt) return;
    try {
      await api.patch(`/leads/${leadId}/lead-type`, { lead_type: lt });
      toast.success(`Lead type set to ${lt}`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const changeRequirement = async (rq) => {
    if (!rq) return;
    try {
      await api.patch(`/leads/${leadId}/requirement`, { requirement: rq });
      toast.success(`Requirement set to ${rq}`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const runEnrich = async () => {    setEnriching(true);
    setSuggestion(null);
    try {
      const { data } = await api.post(`/leads/${leadId}/enrich`);
      setSuggestion(data);
      setEnrichForm({ profession: data.profession, employer: data.employer, segment: data.segment });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setEnriching(false);
    }
  };

  const confirmEnrich = async () => {
    try {
      await api.post(`/leads/${leadId}/enrich/confirm`, {
        ...enrichForm,
        enrich_source: suggestion?.source || "manual research",
        enrich_confidence: suggestion?.confidence ?? null,
        enrich_summary: suggestion?.summary || null,
      });
      toast.success("Enrichment saved");
      setSuggestion(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-stone-900/40 animate-fade-in" onClick={onClose} />
      <div className="relative w-full sm:w-[580px] bg-[#FAFAF9] h-full shadow-2xl border-l border-stone-200 flex flex-col animate-slide-in-right">
        {loading || !lead ? (
          <div className="flex-1 flex items-center justify-center text-stone-400">Loading…</div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 pt-5 pb-4 border-b border-stone-200 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-heading text-2xl tracking-tight text-stone-900 flex items-center gap-2" data-testid="drawer-lead-name">
                    {lead.name}
                    {lead.is_hot && <Flame className="h-5 w-5 text-red-500" />}
                  </h2>
                  <p className="text-sm text-stone-500 mt-0.5">+91 {lead.phone}</p>
                </div>
                <button onClick={onClose} data-testid="drawer-close" className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${STATE_STYLE[lead.state]?.badge}`}>{lead.state}</span>
                {lead.segment && lead.segment !== "Unknown" && canViewAI(user) && (
                  <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${SEGMENT_STYLE[lead.segment]}`}>{lead.segment}</span>
                )}
                {!canEdit && (
                  <span className="text-xs px-2.5 py-1 rounded-full border border-stone-200 bg-stone-50 text-stone-500 flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Read-only
                  </span>
                )}
                {lead.wati_lead_stage && (
                  <span className="text-xs px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 font-medium" data-testid="wati-lead-stage" title="Lead stage from WATI">
                    WATI: {lead.wati_lead_stage}
                  </span>
                )}
                {lead.wati_owner && (
                  <span className="text-xs px-2.5 py-1 rounded-full border border-sky-200 bg-sky-50 text-sky-800 font-medium" data-testid="wati-owner" title="Contact owner in WATI">
                    WATI Owner: {lead.wati_owner}
                  </span>
                )}
                {lead.also_in_jordyblue && isSuperStore(user) && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-stone-900 text-stone-100 font-medium tracking-wide uppercase" data-testid="jordyblue-flag" title="Phone matches a contact in the JordyBlue master sheet">
                    Also in JordyBlue
                  </span>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 flex gap-1 border-b border-stone-200 bg-white">
              {[
                { id: "lead", label: "Lead" },
                { id: "journey", label: "Journey" },
                ...(canViewAI(user) ? [{ id: "research", label: "AI Research" }] : []),
              ].map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`drawer-tab-${t.id}`}
                  className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    tab === t.id ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto col-scroll p-6 space-y-5">
              {tab !== "journey" && (
                <>
                  {/* Hot button */}
                  {tab === "lead" && showHotButton && (
                    <button onClick={toggleHot} data-testid="hot-toggle"
                      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-md border text-sm font-medium transition-colors ${
                        lead.is_hot ? "bg-white border-stone-300 text-stone-700 hover:border-stone-400"
                                    : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"}`}>
                      <Flame className="h-4 w-4" /> {lead.is_hot ? "Remove HOT status" : "Mark as HOT lead"}
                    </button>
                  )}

                  {tab === "research" && (<>
                  {/* Enrichment */}
                  <div className="bg-white border border-stone-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-violet-500" /> Enrichment
                      </p>
                      {CAN_ASSIGN_ROLES.includes(role) && (
                        <button onClick={runEnrich} disabled={enriching} data-testid="enrich-run"
                          className="text-xs px-2.5 py-1 rounded-md bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-60 flex items-center gap-1">
                          {enriching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Re-run
                        </button>
                      )}
                    </div>
                    {lead.enriched_at ? (
                      <>
                        {lead.enrich_status === "failed" && (
                          <div className="mb-2.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 flex items-start gap-1.5" data-testid="enrich-failed-note">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>AI research couldn't complete on the last run. Click <span className="font-semibold">Re-run</span> to try again.</span>
                          </div>
                        )}
                        {lead.enrich_summary ? (
                          <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line" data-testid="lead-enrichment-text">{lead.enrich_summary}</p>
                        ) : lead.enrich_status === "failed" ? null : (
                          <p className="text-sm text-stone-400" data-testid="lead-enrichment-text">No clear public professional footprint was found for this name + city.</p>
                        )}
                        {Array.isArray(lead.enrich_highlights) && lead.enrich_highlights.length > 0 && (
                          <ul className="mt-2.5 space-y-1" data-testid="enrich-highlights">
                            {lead.enrich_highlights.map((h, i) => (
                              <li key={i} className="text-xs text-stone-600 flex gap-1.5">
                                <span className="text-violet-500 mt-0.5 shrink-0">•</span><span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {(lead.profession || lead.employer) && (
                          <p className="text-xs text-stone-500 mt-2.5">{lead.profession}{lead.employer ? ` · ${lead.employer}` : ""}
                            {lead.enrich_confidence != null && ` · ${Math.round(lead.enrich_confidence * 100)}% conf`}</p>
                        )}
                        {lead.enrich_recommended && (
                          <p className="text-xs text-stone-700 mt-2.5 bg-violet-50 border border-violet-100 rounded-md px-2.5 py-1.5">
                            <span className="font-semibold text-violet-700">Suggested approach: </span>{lead.enrich_recommended}</p>
                        )}
                        {Array.isArray(lead.enrich_results) && lead.enrich_results.length > 0 && (
                          <div className="mt-2.5 border-t border-stone-100 pt-2 space-y-1" data-testid="enrich-sources">
                            <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Sources</p>
                            {lead.enrich_results.slice(0, 5).map((r, i) => (
                              <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="block text-xs text-stone-800 hover:underline truncate">{r.title || r.link}</a>
                            ))}
                          </div>
                        )}
                      </>
                    ) : enrichTimedOut ? (
                      <p className="text-sm text-stone-400" data-testid="lead-enrichment-text">
                        Could not auto-generate enrichment just now. Use the Re-run button to try again.
                      </p>
                    ) : (
                      <p className="text-sm text-stone-400 flex items-center gap-2" data-testid="lead-enrichment-text">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" /> Researching live (Google + Gemini)… this takes ~10–20s.
                      </p>
                    )}

                    {suggestion && (
                      <div className="mt-3 border-t border-stone-100 pt-3 space-y-2">
                        {suggestion.summary && <p className="text-sm text-stone-700 whitespace-pre-line" data-testid="enrich-summary">{suggestion.summary}</p>}
                        {Array.isArray(suggestion.highlights) && suggestion.highlights.length > 0 && (
                          <ul className="space-y-1">
                            {suggestion.highlights.map((h, i) => (
                              <li key={i} className="text-xs text-stone-600 flex gap-1.5"><span className="text-violet-500 mt-0.5 shrink-0">•</span><span>{h}</span></li>
                            ))}
                          </ul>
                        )}
                        {suggestion.recommended_approach && (
                          <p className="text-xs text-stone-700 bg-violet-50 border border-violet-100 rounded-md px-2.5 py-1.5">
                            <span className="font-semibold text-violet-700">Suggested approach: </span>{suggestion.recommended_approach}</p>
                        )}
                        {suggestion.results?.map((r, i) => (
                          <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="block text-xs text-stone-800 hover:underline truncate">{r.title || r.link}</a>
                        ))}
                        {["profession", "employer"].map((f) => (
                          <input key={f} value={enrichForm[f]} onChange={(e) => setEnrichForm((p) => ({ ...p, [f]: e.target.value }))}
                            placeholder={f} data-testid={`enrich-${f}`} className="w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white capitalize" />
                        ))}
                        <button onClick={confirmEnrich} data-testid="enrich-confirm"
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md py-1.5 text-sm font-medium flex items-center justify-center gap-1.5">
                          <Check className="h-3.5 w-3.5" /> Save enrichment
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Research links */}
                  {links && CAN_ASSIGN_ROLES.includes(role) && (
                    <div className="flex flex-wrap gap-2">
                      {[["google", "Google"], ["linkedin", "LinkedIn"], ["google_maps", "Maps"]].map(([k, label]) => (
                        <a key={k} href={links[k]} target="_blank" rel="noopener noreferrer" data-testid={`research-link-${k}`}
                          className="text-xs flex items-center gap-1 border border-stone-200 bg-white rounded-md px-2.5 py-1.5 text-stone-600 hover:border-stone-400">
                          {label} <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  )}
                  </>)}

                  {tab === "lead" && (<>
                  {/* Details */}
                  <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
                    <Field icon={Clock} label="Arrived" value={fmtDateTime(lead.created_at)} />
                    <Field icon={MapPin} label="City" value={lead.city} />
                    <div className="flex items-start gap-3">
                      <Tag className="h-4 w-4 text-stone-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Source</p>
                        {canEdit ? (
                          <select value={lead.source || (lead.source_url === "Manual entry" ? "Referral" : "WATI")}
                            onChange={(e) => changeSource(e.target.value)} data-testid="lead-source-select"
                            className="mt-1 w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                            {["WATI", ...LEAD_SOURCES].map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <p className="text-sm text-stone-700" data-testid="lead-source-value">{lead.source || "—"}</p>
                        )}
                      </div>
                    </div>
                    {/* Lead type (editable) + AI segment (read-only, restricted) */}
                    <div className={`grid gap-3 ${canViewAI(user) ? "grid-cols-2" : "grid-cols-1"}`}>
                      <div className="flex items-start gap-3">
                        <Tag className="h-4 w-4 text-stone-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Lead type</p>
                          {canEdit ? (
                            <select value={lead.lead_type || ""} onChange={(e) => changeLeadType(e.target.value)} data-testid="lead-type-select"
                              className="mt-1 w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                              <option value="">— Set type —</option>
                              {LEAD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          ) : (
                            <p className="text-sm text-stone-700" data-testid="lead-type-value">{lead.lead_type || "—"}</p>
                          )}
                        </div>
                      </div>
                      {canViewAI(user) && (
                        <div className="flex items-start gap-3">
                          <Sparkles className="h-4 w-4 text-stone-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">AI segment</p>
                            <p className="text-sm text-stone-700 mt-1.5" data-testid="lead-segment-value" title="Auto-detected by AI (read-only)">{lead.segment || "Unknown"}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Potential order value — staff-entered, visible to anyone who can see the lead. */}
                    <div className="flex items-start gap-3" data-testid="lead-potential-value-field">
                      <IndianRupee className="h-4 w-4 text-stone-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Potential order value</p>
                        {canEdit ? (
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-sm text-stone-500">₹</span>
                            <input
                              type="number"
                              min="0"
                              step="1000"
                              value={pvValue}
                              onChange={(e) => setPvValue(e.target.value)}
                              onBlur={savePotentialValue}
                              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                              disabled={savingPv}
                              data-testid="lead-potential-value-input"
                              placeholder="e.g. 300000"
                              className="flex-1 border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60 disabled:opacity-60"
                            />
                            {pvValue && Number(pvValue) > 0 && (
                              <span className="text-xs text-stone-500 tabular-nums" data-testid="lead-potential-value-formatted">
                                {fmtINR(Number(pvValue))}
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-stone-700 mt-1.5" data-testid="lead-potential-value-display">
                            {lead.potential_order_value ? fmtINR(lead.potential_order_value) : "—"}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Store assignment — Super Store users only. Store-level users never see this. */}
                    {canAssignStore && (
                      <div className="flex items-start gap-3" data-testid="lead-store-field">
                        <Store className="h-4 w-4 text-stone-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Store</p>
                          <select
                            value={lead.store || ""}
                            onChange={(e) => changeStore(e.target.value)}
                            disabled={savingStore}
                            data-testid="lead-store-select"
                            className="mt-1 w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60 disabled:opacity-60">
                            <option value="">— Unassigned (visible only to Super Store) —</option>
                            {stores.filter((s) => s.name !== STORE_SUPER).map((s) => (
                              <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                          </select>
                          {stores.filter((s) => s.name !== STORE_SUPER).length === 0 && (
                            <p className="text-[11px] text-stone-400 mt-1">Add stores in Settings → Stores to enable scoping.</p>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Requirement (editable) */}
                    <div className="flex items-start gap-3">
                      <ShoppingBag className="h-4 w-4 text-stone-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Requirement</p>
                        {canEdit ? (
                          <select value={lead.requirement || ""} onChange={(e) => changeRequirement(e.target.value)} data-testid="requirement-select"
                            className="mt-1 w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                            <option value="">— Set requirement —</option>
                            {requirements.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        ) : (
                          <p className="text-sm text-stone-700" data-testid="requirement-value">{lead.requirement || "—"}</p>
                        )}
                      </div>
                    </div>
                    {lead.first_message && (
                      <div>
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 mb-1">First message</p>
                        <p className="text-sm text-stone-700 leading-relaxed">{lead.first_message}</p>
                      </div>
                    )}
                  </div>

                  {/* Assignment */}
                  {canAssign && (
                    <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
                      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" /> Assignment</p>
                      <div>
                        <label className="text-xs text-stone-500">Backend (calling) assignee</label>
                        <select value={backendId} onChange={(e) => setBackendId(e.target.value)} data-testid="assign-backend"
                          className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                          <option value="">Unassigned</option>
                          {backendUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-stone-500">Interaction team assignee</label>
                        <select value={interId} onChange={(e) => setInterId(e.target.value)} data-testid="assign-interaction"
                          className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                          <option value="">Unassigned</option>
                          {interactionUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </div>
                      <button onClick={saveAssign} data-testid="assign-save"
                        className="w-full border border-stone-300 bg-white hover:border-stone-400 text-stone-800 rounded-md py-2 text-sm font-medium flex items-center justify-center gap-1.5">
                        <Save className="h-3.5 w-3.5" /> Save assignment
                      </button>
                    </div>
                  )}

                  {/* Action panel */}
                  {canEdit ? (
                    <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-4" data-testid="action-panel">
                      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">Work this lead</p>
                      <div className="grid grid-cols-2 gap-2">
                        {["Call", "Chat"].map((a) => (
                          <button key={a} onClick={() => setActionType((cur) => cur === a ? "" : a)} data-testid={`action-type-${a.toLowerCase()}`}
                            className={`flex items-center justify-center gap-2 py-2.5 rounded-md border text-sm font-medium transition-colors ${
                              actionType === a ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 text-stone-700 hover:border-stone-400"}`}>
                            {a === "Call" ? <Phone className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />} {a}
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className="text-xs text-stone-500">Outcome / move to</label>
                        <select value={newState} onChange={(e) => setNewState(e.target.value)} data-testid="action-state-select"
                          className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white">
                          {ALL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      {dateRequired && (
                        <div>
                          <label className="text-xs text-stone-500">Next action date *</label>
                          <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="action-date"
                            className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white" />
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-stone-500">Note</label>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="action-note"
                          placeholder="What happened on this call/chat?" className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white resize-none" />
                      </div>
                      <button onClick={saveAction} disabled={saving} data-testid="action-save"
                        className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Handshake className="h-4 w-4" /> Report activity / Save &amp; update</>}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-stone-100 border border-stone-200 rounded-lg p-4 text-sm text-stone-500 flex items-center gap-2">
                      <Lock className="h-4 w-4" /> This lead is read-only for you{lead.is_hot ? " (HOT lead — handled by Hot Lead Handlers)." : "."}
                    </div>
                  )}

                  {/* Notes history */}
                  {lead.notes?.length > 0 && (
                    <div>
                      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 mb-2">Notes</p>
                      <div className="space-y-2">
                        {lead.notes.slice().reverse().map((n, i) => (
                          <div key={i} className="bg-white border border-stone-200 rounded-lg p-3 text-sm">
                            <p className="text-stone-700">{n.text}</p>
                            <p className="text-xs text-stone-400 mt-1">{n.actor} · {fmtDateTime(n.ts)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Delete (admin / super_admin only) */}
                  {(role === "admin" || role === "super_admin") && (
                    <div className="border-t border-stone-200 pt-4">
                      {!confirmDelete ? (
                        <button onClick={() => setConfirmDelete(true)} data-testid="lead-delete-button"
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-red-200 bg-white text-red-600 text-sm font-medium hover:bg-red-50 transition-colors">
                          <Trash2 className="h-4 w-4" /> Delete this lead
                        </button>
                      ) : (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2" data-testid="lead-delete-confirm">
                          <p className="text-sm text-red-700">Permanently delete <span className="font-semibold">{lead.name}</span> and its history? This cannot be undone.</p>
                          <div className="flex gap-2">
                            <button onClick={deleteLead} disabled={deleting} data-testid="lead-delete-confirm-button"
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-md py-2 text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-60">
                              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Yes, delete
                            </button>
                            <button onClick={() => setConfirmDelete(false)} disabled={deleting}
                              className="flex-1 border border-stone-300 bg-white text-stone-700 rounded-md py-2 text-sm font-medium hover:border-stone-400">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  </>)}
                </>
              )}

              {tab === "journey" && (
                <>
                  {conv && (conv.matched_deals?.length > 0 || conv.matched_orders?.length > 0) && (
                    <div className="bg-white border border-stone-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3"><ShoppingBag className="h-4 w-4 text-green-600" /><p className="text-sm font-semibold text-stone-800">Conversion</p></div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <Stat label="Total order value" value={fmtINR(conv.total_order_value)} />
                        <Stat label="Time to convert" value={conv.time_to_convert_days != null ? `${conv.time_to_convert_days} days` : "—"} />
                      </div>
                      {conv.related_numbers?.length > 1 && <p className="text-xs text-stone-400 mb-2">Bridged numbers: {conv.related_numbers.join(", ")}</p>}
                      {conv.matched_deals.map((d, i) => (
                        <div key={i} className="text-sm border-t border-stone-100 pt-2 mt-2">
                          <p className="text-stone-700">Deal · {fmtINR(d.sale_value)} · closed by {d.closed_by || "—"}</p>
                          <p className="text-xs text-stone-400">{d.date} · {d.lead_type}</p>
                        </div>
                      ))}
                      {conv.matched_orders.map((o, i) => (
                        <div key={i} className="text-sm border-t border-stone-100 pt-2 mt-2">
                          <p className="text-stone-700">Order {o.order_id} · {fmtINR(o.total_value)}</p>
                          <p className="text-xs text-stone-400">{o.products?.join(", ") || "—"}{o.order_date ? ` · ${o.order_date}` : ""}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-stone-800 mb-3">Timeline</p>
                    <div className="relative pl-5" data-testid="journey-timeline">
                      <div className="absolute left-[7px] top-1 bottom-1 w-px bg-stone-200" />
                      {data.timeline.map((ev) => (
                        <div key={ev.id} className="relative mb-4">
                          <span className="absolute -left-5 top-1 h-3.5 w-3.5 rounded-full bg-white border-2 border-stone-400" />
                          <p className="text-sm font-medium text-stone-800">{EVENT_LABEL[ev.event_type] || ev.event_type}</p>
                          {ev.detail && <p className="text-sm text-stone-600">{ev.detail}</p>}
                          <p className="text-xs text-stone-400 mt-0.5">{ev.actor} · {fmtDateTime(ev.ts)}</p>
                        </div>
                      ))}
                      {data.timeline.length === 0 && <p className="text-sm text-stone-400">No activity yet.</p>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-stone-400 mt-0.5" />
      <div>
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400">{label}</p>
        <p className="text-sm text-stone-700 break-all">{value}</p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-stone-50 rounded-lg p-3 border border-stone-100">
      <p className="text-xs text-stone-400">{label}</p>
      <p className="text-lg font-heading text-stone-900">{value}</p>
    </div>
  );
}
